/**
 * Boli Live - Messages Page
 *
 * List of all buyer-seller message threads.
 */

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  MessageSquare,
  User,
  Loader2,
  Package,
} from 'lucide-react';
import { useAuthStore } from '../lib/stores/auth-store';
import { getMessageService } from '@boli/core';
import type { MessageThread } from '@boli/types';

export default function MessagesPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isInitialized, user } = useAuthStore();
  const [threads, setThreads] = useState<MessageThread[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isInitialized && !isAuthenticated) {
      navigate('/profile');
    }
  }, [isInitialized, isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchThreads();
    }
  }, [isAuthenticated]);

  const fetchThreads = async () => {
    try {
      const messageService = getMessageService();
      const response = await messageService.getThreads();
      setThreads(response.threads);
    } catch (error) {
      console.error('Failed to fetch threads:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return date.toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit' });
    } else if (days === 1) {
      return 'Yesterday';
    } else if (days < 7) {
      return date.toLocaleDateString('en-PK', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('en-PK', { month: 'short', day: 'numeric' });
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getUnreadCount = (thread: MessageThread) => {
    if (!user) return 0;
    return thread.buyerId === user.id ? thread.buyerUnreadCount : thread.sellerUnreadCount;
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto px-4 py-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <Link
              to="/"
              className="p-2 hover:bg-secondary rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Messages</h1>
              <p className="text-sm text-muted-foreground">
                {threads.length} conversation{threads.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>

          {/* Thread List */}
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : threads.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="w-8 h-8 text-muted-foreground" />
              </div>
              <h2 className="text-lg font-semibold text-foreground mb-2">No Messages Yet</h2>
              <p className="text-muted-foreground mb-6">
                Your conversations with buyers and sellers will appear here.
              </p>
              <Link
                to="/"
                className="inline-flex px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
              >
                Browse Auctions
              </Link>
            </div>
          ) : (
            <div className="space-y-2">
              {threads.map((thread, index) => {
                const unreadCount = getUnreadCount(thread);
                const isBuyer = user?.id === thread.buyerId;

                return (
                  <motion.div
                    key={thread.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Link
                      to={`/messages/${thread.id}`}
                      className={`block p-4 bg-white rounded-xl border transition-colors ${
                        unreadCount > 0 ? 'border-primary/30 bg-primary/5' : 'border-border hover:border-primary/20'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {/* Avatar */}
                        <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                          {thread.otherParty?.avatar ? (
                            <img
                              src={thread.otherParty.avatar}
                              alt={thread.otherParty.name}
                              className="w-12 h-12 rounded-full object-cover"
                            />
                          ) : (
                            <User className="w-6 h-6 text-muted-foreground" />
                          )}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <span className={`font-medium truncate ${unreadCount > 0 ? 'text-foreground' : 'text-foreground/80'}`}>
                              {thread.otherParty?.name || (isBuyer ? 'Seller' : 'Buyer')}
                            </span>
                            <span className="text-xs text-muted-foreground flex-shrink-0 ml-2">
                              {formatTime(thread.lastMessageAt)}
                            </span>
                          </div>

                          {/* Last message */}
                          <p className={`text-sm truncate ${unreadCount > 0 ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                            {thread.lastMessage || 'No messages yet'}
                          </p>

                          {/* Order info */}
                          {thread.order && (
                            <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                              <Package className="w-3 h-3" />
                              <span>Order {formatCurrency(thread.order.totalAmount)}</span>
                              <span className="text-muted-foreground/50">|</span>
                              <span className="capitalize">{thread.order.status.replace('_', ' ')}</span>
                            </div>
                          )}
                        </div>

                        {/* Unread badge */}
                        {unreadCount > 0 && (
                          <div className="w-6 h-6 bg-primary text-white text-xs font-bold rounded-full flex items-center justify-center flex-shrink-0">
                            {unreadCount > 9 ? '9+' : unreadCount}
                          </div>
                        )}
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
